import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import gsap from "gsap";
import { ScrollTrigger } from "../gsap/ScrollTrigger.min.js";
import { useApiContext } from "../context/ApiContext.jsx";

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
	const navigate = useNavigate();
	const { api, isDemo } = useApiContext();
	const [sessionInput, setSessionInput] = useState("");
	const [hpData, setHpData] = useState(null);
	const [storedSessionId, setStoredSessionId] = useState(() => localStorage.getItem("session_id"));

	// refs
	const rootRef = useRef(null);
	const titleLine1Ref = useRef(null);
	const titleLine2Ref = useRef(null);
	const subtitleRef = useRef(null);
	const featuresGridRef = useRef(null);
	const cardsRef = useRef(null);
	const heroRef = useRef(null);

	useEffect(() => {
		api.get(`/api/trivia/hp-data`)
			.then(function (response) {
				setHpData(response.data)
			})
	}, [api])

	const reduceMotion = useMemo(
		() => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches,
		[]
	);

	// --- Animazioni on mount ---
	useEffect(() => {
		if (reduceMotion) return;
		if (!hpData) return;
		if (!cardsRef.current) return;
		const ctx = gsap.context(() => {
			gsap.fromTo(
				cardsRef.current.querySelectorAll(".mini-card"),
				{ y: 24, opacity: 0, rotate: -3, scale: 0.96 },
				{
					y: 0,
					opacity: 1,
					rotate: 0,
					scale: 1,
					duration: 0.8,
					ease: "power3.out",
					stagger: 0.08,
					delay: 0.2,
				}
			);
		}, rootRef);

		return () => ctx.revert();

		return () => ctx.revert();
	}, [reduceMotion, hpData]);

	// --- Parallax 3D on mousemove ---
	useEffect(() => {
		if (reduceMotion) return;
		if (!hpData) return; // assicura che il blocco con cardsRef sia montato
		if (!heroRef.current || !cardsRef.current) return;

		const el = heroRef.current;
		const card = cardsRef.current;

		const handler = (e) => {
			const rect = el.getBoundingClientRect();
			const cx = rect.left + rect.width / 2;
			const cy = rect.top + rect.height / 2;
			const dx = (e.clientX - cx) / (rect.width / 2); // -1..1
			const dy = (e.clientY - cy) / (rect.height / 2); // -1..1

			gsap.to(card, {
				rotationY: dx * 8,
				rotationX: -dy * 6,
				y: dy * -6,
				transformPerspective: 800,
				transformOrigin: "center",
				ease: "power2.out",
				duration: 0.3,
			});

			// parallax mini-cards
			const minis = card.querySelectorAll(".mini-card");
			minis.forEach((mc, i) => {
				gsap.to(mc, {
					x: dx * (6 + i * 3),
					y: dy * (6 + i * 2),
					duration: 0.3,
					ease: "power2.out",
				});
			});
		};

		const leave = () => {
			gsap.to(cardsRef.current, {
				rotationX: 0,
				rotationY: 0,
				y: 0,
				duration: 0.5,
				ease: "power3.out",
			});
			gsap.to(cardsRef.current.querySelectorAll(".mini-card"), {
				x: 0,
				y: 0,
				duration: 0.5,
				ease: "power3.out",
			});
		};

		el.addEventListener("pointermove", handler);
		el.addEventListener("pointerleave", leave);
		return () => {
			el.removeEventListener("pointermove", handler);
			el.removeEventListener("pointerleave", leave);
		};
	}, [reduceMotion, hpData]);

	useEffect(() => {
		document.querySelector('body').className = 'trivia-home trivia-light-green';
	}, []);

	useEffect(() => {
		const onStorage = (e) => {
			if (e.key === "session_id") {
				setStoredSessionId(localStorage.getItem("session_id"));
			}
		};

		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	const handleStart = (e) => {
		e.preventDefault();
		const s = sessionInput.trim();
		console.log(sessionInput)
		if (s) navigate(`/trivia/${encodeURIComponent(s)}`);
		//else navigate("/trivia");
	};

	const handleResumeYes = (e) => {
		e.preventDefault();
		const sid = localStorage.getItem("session_id");
		if (sid) navigate(`/trivia/${encodeURIComponent(sid)}`);
	};

	const handleResumeNo = (e) => {
		e.preventDefault();
		localStorage.removeItem("session_id");
		setStoredSessionId(null);
		navigate("/trivia");
	};

	return (
		<>
			<img className="bg-image" src={`/img/react-app/bg-oliva-right.png`} />
			<div className="home-landing" ref={rootRef}>

				<section className="hero container" ref={heroRef}>
					<div className="hero__copy">
						<h1 className="hero__title">
							<div className="d-block mb-2" style={{ fontSize: "2.9rem", lineHeight: "1" }} ref={titleLine1Ref}>Benvenuto nel</div>
							<span className="title-gradient d-block" style={{ fontSize: "2.9rem", lineHeight: "1" }} ref={titleLine2Ref}>Trivia Game - Gioca con Oleario</span>
						</h1>
						<p className="hero__subtitle fs-6" ref={subtitleRef}>
							Il Trivia Game di Oleario, dedicato agli studenti delle Scuole di ogni ordine e grado
							e a tutti coloro che vogliono approfondire la conoscenza del mondo dell’olio,
							mette a disposizione domande suddivise per tematiche con diversi gradi di difficoltà.<br />
							Prova a scoprire quanto ne sai del mondo dell’Olio!
						</p>

						{storedSessionId ? (
							<div className="hero__cta mt-0">
								<h3 className="mb-2 fs-2 lh-1 fw-bold mb-4">
									Hai una sezione di gioco attiva. Vuoi riprenderla?
								</h3>
								<div className="d-flex gap-2 flex-wrap my-3">
									<a className="btn btn-primary" href={`/trivia/${encodeURIComponent(storedSessionId)}`} onClick={handleResumeYes}>
										Sì
									</a>
									<button className="btn btn-outline-light" type="button" onClick={handleResumeNo}>
										No
									</button>
								</div>
							</div>
						) : (
							<form className="hero__cta" onSubmit={handleStart}>
								<div className="input-wrap">
									<input
										type="text"
										placeholder="Incolla il codice di accesso"
										value={sessionInput}
										onChange={(e) => setSessionInput(e.target.value)}
										aria-label="Session ID"
									/>
									<button className="btn btn-primary" type="submit">
										<span>Invia</span>
										<i aria-hidden="true" className="shine" />
									</button>
								</div>
								<small className="helper lh-sm">
									Non hai il codice?<br />
									Nessun problema, chiedi al tuo docente di iscrivere il tuo istituto.<br />
									Oppure <a href="/Identity/Account/RegisterInstitute" className="text-white fw-semibold" target="_blank">vai al modulo di registrazione</a>.
								</small>
							</form>
						)}

						<div className="hero__badges" aria-hidden="true">
							<span className="badge">#olioevo</span>
							<span className="badge">#paesaggio</span>
							<span className="badge">#biodiversità</span>
							<span className="badge">#qualità</span>
							<span className="badge">#etichettatura</span>
							<span className="badge">#quiz divulgativi</span>
							<span className="badge">#impara giocando</span>
							<span className="badge">#gioco didattico</span>
							<span className="badge">#benessere</span>
							<span className="badge">#nutraceutico</span>
							<span className="badge">#farmaco alimento</span>
						</div>
					</div>

					{hpData && (
						<>
							<div className="hero__visual" aria-hidden="true">
								<div className="cards-stack" ref={cardsRef}>
									<div className="mini-card">
										<span className="mini-title">Studenti</span>
										<span className="mini-score">{hpData.sessions}</span>
									</div>
									<div className="mini-card">
										<span className="mini-title">Istituti</span>
										<span className="mini-score">{hpData.institutes}</span>
									</div>
									<div className="mini-card">
										<span className="mini-title">Partite giocate</span>
										<span className="mini-score">{hpData.rooms}</span>
									</div>
								</div>
							</div>
						</>
					)}

				</section>

				<section className="cta container d-md-flex justify-content-center">
					<div className="mx-4">
						<h2 className="cta__title fs-5">Sei un docente e vuoi portare Oleario nella tua scuola?</h2>
						<a className="btn btn-primary" href="/Identity/Account/RegisterInstitute" target="_blank">
							Registrati subito
						</a>
					</div>
					<div className="mx-4 mt-5 mt-md-0">
						<h2 className="cta__title fs-5">
							<span className="ms-2" style={{ animation: "timer-warn-pulse 1s ease-out infinite" }}>Crea una partita immediata, senza registrazione!</span>
						</h2>
						<a className="btn btn-primary" href="/trivia/quick-match">
							Scopri come
						</a>
					</div>
				</section>
			</div>
		</>

	);
}
